import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import {
  ReceivableDocumentType,
  PendingReceivableDocument,
} from "../../utils/types";
import {
  documentFormSchema,
  DocumentFormValues,
  generateTempDocumentId,
} from "./utils";
import { DocumentFormFields } from "./DocumentFormFields";
import { DocumentFileUpload } from "./DocumentFileUpload";
import { useTranslation } from "@/shared/i18n";

interface DocumentCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: DocumentFormValues) => Promise<void>;
  disabled?: boolean;
  isLocked?: boolean;
}

export function DocumentCreateDialog({
  open,
  onOpenChange,
  onSubmit,
  disabled = false,
  isLocked = false,
}: DocumentCreateDialogProps) {
  const { t } = useTranslation('receivables-components');
  const [fileInputKey, setFileInputKey] = useState(0);
  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      documentType: ReceivableDocumentType.INVOICE_FROM_PLATFORM,
      description: "",
      documentUrl: "",
      file: undefined,
    },
  });

  const isSubmitting = form.formState.isSubmitting;
  const selectedFile = form.watch("file");
  const documentUrl = form.watch("documentUrl");

  useEffect(() => {
    const hasUrl = typeof documentUrl === "string" && documentUrl.trim() !== "";
    if (selectedFile instanceof File || hasUrl) {
      form.clearErrors("file");
    }
  }, [selectedFile, documentUrl, form]);

  useEffect(() => {
    if (!open) {
      form.reset({
        documentType: ReceivableDocumentType.INVOICE_FROM_PLATFORM,
        description: "",
        documentUrl: "",
        file: undefined,
      });
      setFileInputKey((prev) => prev + 1);
      form.clearErrors();
    }
  }, [open, form]);

  const handleSubmit = async (values: DocumentFormValues) => {
    if (disabled || isSubmitting || isLocked) return;
    await onSubmit(values);
  };

  const handleFileInputKeyChange = () => {
    setFileInputKey((prev) => prev + 1);
  };

  const isBusy = disabled || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={(open) => !isLocked && onOpenChange(open)}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('newDocument')}</DialogTitle>
          <DialogDescription>
            {t('newDocumentDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(handleSubmit)(event);
            }}
            className="space-y-6"
          >
            <DocumentFormFields form={form} disabled={isBusy} />

            <DocumentFileUpload
              form={form}
              fileInputKey={fileInputKey}
              onFileInputKeyChange={handleFileInputKeyChange}
              disabled={isBusy}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isBusy} onClick={(e) => e.stopPropagation()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('saving')}
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('save')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

